<script>
    import { format_age_range_compact } from './utils/format_ages';
    import { img_cdn } from './utils/image';
    import { site_origin } from './stores/globals';

    /**
     * ItemList schema for product list contexts (homepage, category pages).
     * Each item embeds the same fields the user sees on the product card:
     * name, image, lead/description, age range, offer. Skips heavy detail-page
     * fields (reviews, video trailer, hasCourseInstance). The @id matches the
     * canonical detail-page URL so Google merges the two schemas.
     *
     * Items are typed per product.type, so mixed arrays (e.g. courses + worksheets)
     * are supported.
     */

    /** @type {any[]} */
    export let products;

    // Extras and books carry no listed field — only an explicit false hides.
    const listed_products = products.filter((p) => p.listed !== false);

    /** @type {string | undefined} */
    export let name = undefined;

    const type_to_path = {
        course: 'courses',
        worksheet: 'worksheets',
        bundle: 'bundles'
    };

    const organization = {
        '@type': 'Organization',
        name: 'Critikid',
        url: site_origin
    };

    /** @param {string | undefined | null} html */
    function strip_html(html) {
        return html ? html.replace(/<[^>]*>/g, '').trim() : undefined;
    }

    /** @param {any} price */
    function build_offer(price) {
        if (!price) return undefined;
        return {
            '@type': 'Offer',
            price: price.price?.toString() ?? '0',
            priceCurrency: price.price_fx,
            availability: 'https://schema.org/OnlineOnly'
        };
    }

    /** @param {any} product */
    function build_item(product) {
        const t = product.type;
        const url = `${site_origin}/${type_to_path[t]}/${product.slug}`;
        const image = product.icon ? img_cdn(product.icon) : undefined;
        const description = strip_html(product.lead);
        const offers = build_offer(product.price);
        const has_age = !!product.min_age || !!product.max_age;
        const age_str = format_age_range_compact(
            product.min_age,
            product.max_age
        );

        if (t === 'course') {
            return {
                '@type': ['Product', 'Course'],
                '@id': url,
                url,
                name: product.title,
                image,
                description,
                provider: organization,
                courseMode: 'online',
                typicalAgeRange: age_str,
                offers
            };
        }

        const audience = has_age
            ? {
                  '@type': 'PeopleAudience',
                  suggestedMinAge: product.min_age || 5,
                  ...(product.max_age && { suggestedMaxAge: product.max_age })
              }
            : undefined;

        if (t === 'worksheet') {
            return {
                '@type': 'Product',
                '@id': url,
                url,
                name: product.title,
                image,
                description,
                brand: organization,
                audience,
                offers
            };
        }

        // bundle
        const has_part = product.items?.map((/** @type {any} */ p) => {
            // Extras have no detail page — describe them inline.
            if (p.type === 'extra') {
                return {
                    '@type': 'Product',
                    ...(p.url && { '@id': p.url, url: p.url }),
                    name: p.title,
                    image: p.icon ? img_cdn(p.icon) : undefined,
                    description: strip_html(p.lead),
                    offers: build_offer(p.price)
                };
            }

            // Course/Worksheet: slim reference; canonical data lives on detail page.
            const part_url = `${site_origin}/${type_to_path[p.type]}/${p.slug}`;
            return {
                '@type':
                    p.type === 'course' ? ['Product', 'Course'] : 'Product',
                '@id': part_url,
                url: part_url,
                name: p.title
            };
        });

        return {
            '@type': 'Product',
            '@id': url,
            url,
            name: product.title,
            image,
            description,
            brand: organization,
            audience,
            offers,
            hasPart: has_part
        };
    }

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        ...(name && { name }),
        numberOfItems: listed_products.length,
        itemListElement: listed_products.map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            item: build_item(p)
        }))
    };
</script>

<svelte:head>
    {#if listed_products.length > 0}
        {@html `<script type="application/ld+json">${JSON.stringify(schema).replace(/</g, '\\u003c').replace(/>/g, '\\u003e')}</script>`}
    {/if}
</svelte:head>
